// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect } from "react";
import { Key, Loader2, X, Trash2 } from "lucide-react";
import { roleService } from "../../services/roleService";
import InlineNotification from "../InlineNotification";
import useInlineNotification from "../../hooks/useInlineNotification";
import { useRoutes } from "../../hooks/useRoutes";
import { hasSuperAdminAccess } from "../../config/roleConfig";
import GlobalAdminHeader from "./GlobalAdminHeader";
import PageLoader from "../PageLoader";

const Permission = () => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingPermission, setDeletingPermission] = useState(null);
  const [newPermission, setNewPermission] = useState({
    name: "",
    code: "",
    description: "",
    resource: "",
    action: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { notification, showSuccess, showError } = useInlineNotification();

  // Only Super Admin can add / delete permissions
  const { userRoles } = useRoutes();
  const isSuperAdmin = hasSuperAdminAccess(userRoles || []);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const response = await roleService.rolePermissionsList();
      const permissionsArray = Array.isArray(response)
        ? response
        : response?.data
          ? response.data
          : response?.items
            ? response.items
            : [];

      setPermissions(permissionsArray);
    } catch (err) {
      console.error("Error fetching permissions:", err);
      setError("Fehler beim Laden der Berechtigungen");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPermission = async () => {
    if (
      !newPermission.name.trim() ||
      !newPermission.code.trim() ||
      !newPermission.resource.trim() ||
      !newPermission.action.trim()
    ) {
      showError("Bitte füllen Sie alle Pflichtfelder aus.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const response = await roleService.createPermission(newPermission);
      setPermissions((prev) => [...prev, response]);
      setNewPermission({
        name: "",
        code: "",
        description: "",
        resource: "",
        action: "",
      });
      setShowAddModal(false);
      showSuccess("Berechtigung erfolgreich erstellt!");
    } catch (err) {
      console.error("Error creating permission:", err);

      let errorMessage = "Fehler beim Erstellen der Berechtigung";

      if (err.response?.status === 422) {
        const validationErrors = err.response?.data;
        if (validationErrors?.detail) {
          if (Array.isArray(validationErrors.detail)) {
            const fieldErrors = validationErrors.detail
              .map((error) => `${error.loc?.join(".")}: ${error.msg}`)
              .join(", ");
            errorMessage = `Validierungsfehler: ${fieldErrors}`;
          } else {
            errorMessage = `Validierungsfehler: ${validationErrors.detail}`;
          }
        }
      } else if (err.response?.status === 409) {
        errorMessage = "Diese Berechtigung existiert bereits.";
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePermission = (permission) => {
    setDeletingPermission(permission);
    setShowDeleteModal(true);
  };

  const confirmDeletePermission = async () => {
    if (!deletingPermission) return;

    setDeleting(true);
    try {
      await roleService.deletePermission(deletingPermission.permission_id);
      setPermissions((prev) =>
        prev.filter(
          (p) => p.permission_id !== deletingPermission.permission_id,
        ),
      );
      setShowDeleteModal(false);
      setDeletingPermission(null);
      showSuccess("Berechtigung erfolgreich gelöscht!");
    } catch (err) {
      console.error("Error deleting permission:", err);

      let errorMessage = "Fehler beim Löschen der Berechtigung";

      if (err.response?.status === 404) {
        errorMessage = "Berechtigung nicht gefunden.";
      } else if (err.response?.status === 409) {
        errorMessage =
          "Berechtigung kann nicht gelöscht werden (möglicherweise noch in Verwendung).";
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      showError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingPermission(null);
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const groupPermissionsByResource = () => {
    const grouped = {};

    permissions.forEach((permission) => {
      const resource = permission.resource || "other";

      if (!grouped[resource]) {
        grouped[resource] = [];
      }
      grouped[resource].push(permission);
    });

    return grouped;
  };

  const groupedPermissions = groupPermissionsByResource();

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-2">{error}</p>
          <button
            onClick={fetchPermissions}
            className="px-4 py-2 bg-blue-500 dark:bg-blue-600 text-white rounded-lg hover:bg-blue-600 dark:hover:bg-blue-500 transition-colors"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="relative">
        <div className="flex flex-col w-full">
          <GlobalAdminHeader
            title="Berechtigungen"
            buttons={
              isSuperAdmin
                ? [
                    {
                      text: "Berechtigung hinzufügen",
                      icon: <Key className="w-4 h-4" />,
                      onClick: () => setShowAddModal(true),
                      className:
                        "flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-color text-white font-medium shadow-lg hover:opacity-90 transition-opacity",
                    },
                  ]
                : []
            }
          />

          <div className="mx-auto px-4 lg:px-4 sm:mt-6 mt-3 pb-6 w-full">
            <InlineNotification notification={notification} />

            {loading ? (
              <PageLoader message="Lade Berechtigungen..." />
            ) : (
              <div className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 overflow-hidden">
                {Object.keys(groupedPermissions).length === 0 ? (
                  <div className="p-8 text-center text-gray-500 dark:text-slate-400">
                    Keine Berechtigungen gefunden
                  </div>
                ) : (
                  Object.entries(groupedPermissions).map(
                    ([resource, resourcePermissions]) => (
                      <div
                        key={resource}
                        className="border-b border-gray-200 dark:border-slate-600 last:border-b-0"
                      >
                        <div className="p-4">
                          <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100 capitalize mb-3">
                            {resource} Permissions
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {resourcePermissions.map((permission) => (
                              <div
                                key={permission.permission_id}
                                className="flex justify-between items-center p-3 bg-gray-50 dark:bg-slate-700/50 rounded dark:border dark:border-slate-600"
                              >
                                <div className="flex-1">
                                  <p className="font-medium text-gray-800 dark:text-slate-200">
                                    {permission.name || "Unnamed Permission"}
                                  </p>
                                  <p className="text-sm text-gray-600 dark:text-slate-400">
                                    Code: {permission.code || "N/A"}
                                  </p>
                                  {permission.description && (
                                    <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                                      {permission.description}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="text-right">
                                    <span className="px-2 py-1 text-xs font-medium bg-primary-color-tint primary-color rounded">
                                      {permission.action || "permission"}
                                    </span>
                                  </div>
                                  {isSuperAdmin && (
                                    <button
                                      onClick={() =>
                                        handleDeletePermission(permission)
                                      }
                                      className="p-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                                      title="Berechtigung löschen"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ),
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Permission Modal */}
      {isSuperAdmin && showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg dark:border dark:border-slate-700 p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100">
                Neue Berechtigung hinzufügen
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-200 rounded-lg text-sm border border-red-200 dark:border-red-800">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={newPermission.name}
                  onChange={(e) =>
                    setNewPermission((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:placeholder-slate-400"
                  placeholder="z.B. Create Users"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Code *
                </label>
                <input
                  type="text"
                  value={newPermission.code}
                  onChange={(e) =>
                    setNewPermission((prev) => ({
                      ...prev,
                      code: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:placeholder-slate-400"
                  placeholder="z.B. user.create"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Resource *
                </label>
                <input
                  type="text"
                  value={newPermission.resource}
                  onChange={(e) =>
                    setNewPermission((prev) => ({
                      ...prev,
                      resource: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:placeholder-slate-400"
                  placeholder="z.B. user"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Action *
                </label>
                <select
                  value={newPermission.action}
                  onChange={(e) =>
                    setNewPermission((prev) => ({
                      ...prev,
                      action: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Action auswählen...</option>
                  <option value="create">Create</option>
                  <option value="read">Read</option>
                  <option value="update">Update</option>
                  <option value="delete">Delete</option>
                  <option value="list">List</option>
                  <option value="manage">Manage</option>
                  <option value="approve">Approve</option>
                  <option value="reject">Reject</option>
                  <option value="export">Export</option>
                  <option value="import">Import</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Beschreibung
                </label>
                <textarea
                  value={newPermission.description}
                  onChange={(e) =>
                    setNewPermission((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:placeholder-slate-400"
                  rows="3"
                  placeholder="Beschreibung der Berechtigung..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-200 border border-rose-300 dark:border-rose-700 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/50 transition-colors"
                disabled={submitting}
              >
                Abbrechen
              </button>
              <button
                onClick={handleAddPermission}
                disabled={
                  submitting ||
                  !newPermission.name.trim() ||
                  !newPermission.code.trim() ||
                  !newPermission.resource.trim() ||
                  !newPermission.action.trim()
                }
                className="px-4 py-2 bg-blue-500 dark:bg-blue-600 text-white rounded-lg hover:bg-blue-600 dark:hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Erstelle...
                  </>
                ) : (
                  "Erstellen"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isSuperAdmin && showDeleteModal && deletingPermission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg dark:border dark:border-slate-700 p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100">
                Berechtigung löschen
              </h3>
              <button
                onClick={closeDeleteModal}
                className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200"
                disabled={deleting}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <p className="text-gray-700 dark:text-slate-300 mb-2">
                Sind Sie sicher, dass Sie die Berechtigung löschen möchten?
                <strong className="dark:text-slate-100">
                  {deletingPermission.name}
                </strong>{" "}
              </p>
              <div className="bg-gray-50 dark:bg-slate-700/50 p-3 rounded-lg dark:border dark:border-slate-600">
                <p className="text-sm text-gray-600 dark:text-slate-300">
                  <strong className="dark:text-slate-100">Code:</strong>{" "}
                  {deletingPermission.code}
                </p>
                <p className="text-sm text-gray-600 dark:text-slate-300">
                  <strong className="dark:text-slate-100">Resource:</strong>{" "}
                  {deletingPermission.resource}
                </p>
                <p className="text-sm text-gray-600 dark:text-slate-300">
                  <strong className="dark:text-slate-100">Action:</strong>{" "}
                  {deletingPermission.action}
                </p>
              </div>
              <p className="text-gray-500 dark:text-slate-400 mt-2 text-sm">
                Diese Aktion kann nicht rückgängig gemacht werden.
              </p>
            </div>
            /
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeDeleteModal}
                className="px-4 py-2 text-rose-700 dark:text-rose-200 border border-rose-300 dark:border-rose-700 bg-rose-100 dark:bg-rose-900/30 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/50 transition-colors"
                disabled={deleting}
              >
                Abbrechen
              </button>
              <button
                onClick={confirmDeletePermission}
                disabled={deleting}
                className="px-4 py-2 bg-red-500 dark:bg-red-600 text-white rounded-lg hover:bg-red-600 dark:hover:bg-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Lösche...
                  </>
                ) : (
                  "Löschen"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Permission;
